import { getAllBrandsLink } from "@faire/retailer-visitor-shared/consts/allBrandsLinks";
import {
  popSignUp,
  SignUpElement,
  SignUpType,
} from "@faire/retailer-visitor-shared/lib/popSignUp";
import { trackSignupModalOpenCatnavFaqCtaClick } from "@faire/web-api--source/events/signup/click/modalOpenCatnavFaqCta";
import { ICategory } from "@faire/web-api--source/indigofair/data/ICategory";
import { path as c1CategoryPath } from "@faire/web-api--source/routes/www/category/c1CategoryUrlId";
import { path as c2CategoryPath } from "@faire/web-api--source/routes/www/category/c1CategoryUrlId/subcategory/c2CategoryUrlId";
import { DEPRECATED_backwardsCompatibleValues } from "@faire/web/common/localization";
import { Button } from "@faire/web/slate/Button";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { History } from "history";
import React from "react";

const getCtaPath = (
  c1UrlId: string | undefined,
  c2UrlId: string | undefined
) => {
  if (!c1UrlId) {
    return getAllBrandsLink();
  }

  if (!c2UrlId) {
    return c1CategoryPath(c1UrlId);
  }

  return c2CategoryPath(c1UrlId, c2UrlId);
};

const getC2LinkFromIUrlIds = (
  c1UrlId: string | undefined,
  c2UrlId: string | undefined
) =>
  function (chunks: React.ReactNode) {
    return (
      <Button variant="tertiary" linkTo={getCtaPath(c1UrlId, c2UrlId)}>
        {chunks}
      </Button>
    );
  };

const getSignUpLink = (
  history: History,
  chunks: React.ReactNode,
  sourceId: string
) => (
  <Button
    variant="tertiary"
    onClick={() =>
      popSignUp(
        history,
        SignUpType.GENERIC,
        trackSignupModalOpenCatnavFaqCtaClick,
        {
          element: SignUpElement.BELOW_FOLD,
          sourceId: `category_page_c1_answer_content_${sourceId}`,
        }
      )
    }
  >
    {chunks}
  </Button>
);

// All StrictLocalMsg below have been explicitly discussed offline regarding the usage of the placeholders and their
// usage in translation will be checked thoroughly
export const getAnswer1 = (
  history: History,
  c1: ICategory,
  c2: ICategory[] | undefined
) => {
  if (!c2) {
    return null;
  }

  // since we do not support dynamic length lists for translations, we are enumerating a separate localized message for
  // each variation in length of the list of c2 links (length of 1 to 5)
  switch (c2.length) {
    case 1:
      return (
        <Typography>
          <StrictLocalMsg
            id="kqztjR"
            defaultMessage="On Faire, there's a variety of products in {categoryName} available for purchase at wholesale prices for your store. Some of the top-selling {categoryName} products on Faire include: <C2FirstLinkFormatting>{c2FirstLinkName}</C2FirstLinkFormatting>, and more. <StartShoppingLinkFormatting>Start shopping today</StartShoppingLinkFormatting>."
            description={{
              text: 'first answer in a list of FAQs in the C1 category page, categoryName can refer to categories like "Pets", "Women\'s", "Beauty", etc. Please ensure that your translations adapt to various terms (e.g: products in this category "{categoryName}"',
            }}
            values={{
              ...DEPRECATED_backwardsCompatibleValues<Record<string, string>>({
                categoryName: c1.name,
                c2FirstLinkName: c2[0]?.name,
              }),
              C2FirstLinkFormatting: getC2LinkFromIUrlIds(
                c1.url_id,
                c2[0]?.url_id
              ),
              StartShoppingLinkFormatting: (chunks: React.ReactNode) =>
                getSignUpLink(history, chunks, c1.name ?? ""),
            }}
          />
        </Typography>
      );
    case 2:
      return (
        <Typography>
          <StrictLocalMsg
            id="ODpOwP"
            defaultMessage="On Faire, there's a variety of products in {categoryName} available for purchase at wholesale prices for your store. Some of the top-selling {categoryName} products on Faire include: <C2FirstLinkFormatting>{c2FirstLinkName}</C2FirstLinkFormatting>, <C2SecondLinkFormatting>{c2SecondLinkName}</C2SecondLinkFormatting>, and more. <StartShoppingLinkFormatting>Start shopping today</StartShoppingLinkFormatting>."
            description={{
              text: 'first answer in a list of FAQs in the C1 category page, categoryName can refer to categories like "Pets", "Women\'s", "Beauty", etc. Please ensure that your translations adapt to various terms (e.g: products in this category "{categoryName}"',
            }}
            values={{
              ...DEPRECATED_backwardsCompatibleValues<Record<string, string>>({
                categoryName: c1.name,
                c2FirstLinkName: c2[0]?.name,
                c2SecondLinkName: c2[1]?.name,
              }),
              C2FirstLinkFormatting: getC2LinkFromIUrlIds(
                c1.url_id,
                c2[0]?.url_id
              ),
              C2SecondLinkFormatting: getC2LinkFromIUrlIds(
                c1.url_id,
                c2[1]?.url_id
              ),
              StartShoppingLinkFormatting: (chunks: React.ReactNode) =>
                getSignUpLink(history, chunks, c1.name ?? ""),
            }}
          />
        </Typography>
      );
    case 3:
      return (
        <Typography>
          <StrictLocalMsg
            id="C23kua"
            defaultMessage="On Faire, there's a variety of products in {categoryName} available for purchase at wholesale prices for your store. Some of the top-selling {categoryName} products on Faire include: <C2FirstLinkFormatting>{c2FirstLinkName}</C2FirstLinkFormatting>, <C2SecondLinkFormatting>{c2SecondLinkName}</C2SecondLinkFormatting>, <C2ThirdLinkFormatting>{c2ThirdLinkName}</C2ThirdLinkFormatting>, and more. <StartShoppingLinkFormatting>Start shopping today</StartShoppingLinkFormatting>."
            description={{
              text: 'first answer in a list of FAQs in the C1 category page, categoryName can refer to categories like "Pets", "Women\'s", "Beauty", etc. Please ensure that your translations adapt to various terms (e.g: products in this category "{categoryName}"',
            }}
            values={{
              ...DEPRECATED_backwardsCompatibleValues<Record<string, string>>({
                categoryName: c1.name,
                c2FirstLinkName: c2[0]?.name,
                c2SecondLinkName: c2[1]?.name,
                c2ThirdLinkName: c2[2]?.name,
              }),
              C2FirstLinkFormatting: getC2LinkFromIUrlIds(
                c1.url_id,
                c2[0]?.url_id
              ),
              C2SecondLinkFormatting: getC2LinkFromIUrlIds(
                c1.url_id,
                c2[1]?.url_id
              ),
              C2ThirdLinkFormatting: getC2LinkFromIUrlIds(
                c1.url_id,
                c2[2]?.url_id
              ),
              StartShoppingLinkFormatting: (chunks: React.ReactNode) =>
                getSignUpLink(history, chunks, c1.name ?? ""),
            }}
          />
        </Typography>
      );
    case 4:
      return (
        <Typography>
          <StrictLocalMsg
            id="1PBac9"
            defaultMessage="On Faire, there's a variety of products in {categoryName} available for purchase at wholesale prices for your store. Some of the top-selling {categoryName} products on Faire include: <C2FirstLinkFormatting>{c2FirstLinkName}</C2FirstLinkFormatting>, <C2SecondLinkFormatting>{c2SecondLinkName}</C2SecondLinkFormatting>, <C2ThirdLinkFormatting>{c2ThirdLinkName}</C2ThirdLinkFormatting>, <C2FourthLinkFormatting>{c2FourthLinkName}</C2FourthLinkFormatting>, and more. <StartShoppingLinkFormatting>Start shopping today</StartShoppingLinkFormatting>."
            description={{
              text: 'first answer in a list of FAQs in the C1 category page, categoryName can refer to categories like "Pets", "Women\'s", "Beauty", etc. Please ensure that your translations adapt to various terms (e.g: products in this category "{categoryName}"',
            }}
            values={{
              ...DEPRECATED_backwardsCompatibleValues<Record<string, string>>({
                categoryName: c1.name,
                c2FirstLinkName: c2[0]?.name,
                c2SecondLinkName: c2[1]?.name,
                c2ThirdLinkName: c2[2]?.name,
                c2FourthLinkName: c2[3]?.name,
              }),
              C2FirstLinkFormatting: getC2LinkFromIUrlIds(
                c1.url_id,
                c2[0]?.url_id
              ),
              C2SecondLinkFormatting: getC2LinkFromIUrlIds(
                c1.url_id,
                c2[1]?.url_id
              ),
              C2ThirdLinkFormatting: getC2LinkFromIUrlIds(
                c1.url_id,
                c2[2]?.url_id
              ),
              C2FourthLinkFormatting: getC2LinkFromIUrlIds(
                c1.url_id,
                c2[3]?.url_id
              ),
              StartShoppingLinkFormatting: (chunks: React.ReactNode) =>
                getSignUpLink(history, chunks, c1.name ?? ""),
            }}
          />
        </Typography>
      );
    case 5:
      return (
        <Typography>
          <StrictLocalMsg
            id="mgGDjS"
            defaultMessage="On Faire, there's a variety of products in {categoryName} available for purchase at wholesale prices for your store. Some of the top-selling {categoryName} products on Faire include: <C2FirstLinkFormatting>{c2FirstLinkName}</C2FirstLinkFormatting>, <C2SecondLinkFormatting>{c2SecondLinkName}</C2SecondLinkFormatting>, <C2ThirdLinkFormatting>{c2ThirdLinkName}</C2ThirdLinkFormatting>, <C2FourthLinkFormatting>{c2FourthLinkName}</C2FourthLinkFormatting>, <C2FifthLinkFormatting>{c2FifthLinkName}</C2FifthLinkFormatting>, and more. <StartShoppingLinkFormatting>Start shopping today</StartShoppingLinkFormatting>."
            description={{
              text: 'first answer in a list of FAQs in the C1 category page, categoryName can refer to categories like "Pets", "Women\'s", "Beauty", etc. Please ensure that your translations adapt to various terms (e.g: products in this category "{categoryName}"',
            }}
            values={{
              ...DEPRECATED_backwardsCompatibleValues<Record<string, string>>({
                categoryName: c1.name,
                c2FirstLinkName: c2[0]?.name,
                c2SecondLinkName: c2[1]?.name,
                c2ThirdLinkName: c2[2]?.name,
                c2FourthLinkName: c2[3]?.name,
                c2FifthLinkName: c2[4]?.name,
              }),
              C2FirstLinkFormatting: getC2LinkFromIUrlIds(
                c1.url_id,
                c2[0]?.url_id
              ),
              C2SecondLinkFormatting: getC2LinkFromIUrlIds(
                c1.url_id,
                c2[1]?.url_id
              ),
              C2ThirdLinkFormatting: getC2LinkFromIUrlIds(
                c1.url_id,
                c2[2]?.url_id
              ),
              C2FourthLinkFormatting: getC2LinkFromIUrlIds(
                c1.url_id,
                c2[3]?.url_id
              ),
              C2FifthLinkFormatting: getC2LinkFromIUrlIds(
                c1.url_id,
                c2[4]?.url_id
              ),
              StartShoppingLinkFormatting: (chunks: React.ReactNode) =>
                getSignUpLink(history, chunks, c1.name ?? ""),
            }}
          />
        </Typography>
      );
    default:
      return null;
  }
};
